import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n";
import PlatformGrid from "@/components/public/PlatformGrid";
import Seo from "@/components/public/Seo";
import { BookOpen, ShieldCheck, ScanLine, ExternalLink } from "lucide-react";
import { fetchArticles, fetchPublishedHomeContent } from "@/lib/api";
import ArticleCard from "@/components/public/ArticleCard";
import HomeContentSections from "@/components/public/HomeContentSections";
import AdsterraNativeBanner from "@/components/public/AdsterraNativeBanner";
import { DEFAULT_HOME_CONTENT } from "@/content/homePageContent";

function isValidHomeContent(content) {
  return Boolean(content && typeof content === "object"
    && typeof content.hero_title === "string" && content.hero_title.trim()
    && typeof content.introduction === "string" && content.introduction.trim()
    && Array.isArray(content.sections) && Array.isArray(content.faqs));
}

export default function HomePage({ initialHomeContent, initialArticles }) {
  const { settings, platforms } = useApp();
  const { t } = useI18n();
  const supportedPlatforms = platforms.filter((platform) => platform.enabled && platform.supported !== false && platform.slug);
  const platformNames = supportedPlatforms.map((platform) => platform.name).filter(Boolean);
  const hasAvailableTools = supportedPlatforms.length > 0;
  const defaultDescription = hasAvailableTools
    ? `راجع الأدوات المتاحة في ARAN TOOLS واقرأ إرشادات كل أداة قبل استخدامها. المنصات الظاهرة كمتاحة: ${platformNames.join("، ")}.`
    : "تعرّف على مكتبة ARAN TOOLS التعليمية وإرشادات الخصوصية وحقوق المحتوى. خدمات تحليل الروابط وتنزيل الوسائط متوقفة حالياً.";
  const hasInitialHomeContent = initialHomeContent !== undefined;
  const [homeContent, setHomeContent] = useState(() => hasInitialHomeContent
    ? (isValidHomeContent(initialHomeContent) ? initialHomeContent : DEFAULT_HOME_CONTENT)
    : null);
  const [homeContentReady, setHomeContentReady] = useState(hasInitialHomeContent);
  const displayHomeContent = hasAvailableTools ? homeContent : DEFAULT_HOME_CONTENT;
  const description = hasAvailableTools
    ? homeContent?.seo_description || settings.seo_description || defaultDescription
    : defaultDescription;
  const [articles, setArticles] = useState(() => Array.isArray(initialArticles) ? initialArticles : []);

  useEffect(() => {
    let active = true;
    if (!Array.isArray(initialArticles)) {
      fetchArticles({ page: 1, per_page: 8, language: "ar", sort: "latest" })
        .then((result) => { if (active) setArticles(result.items || []); })
        .catch(() => { if (active) setArticles([]); });
    }
    if (!hasInitialHomeContent) {
      fetchPublishedHomeContent()
        .then((result) => {
          if (!active) return;
          setHomeContent(isValidHomeContent(result?.content) ? result.content : DEFAULT_HOME_CONTENT);
          setHomeContentReady(true);
        })
        .catch(() => {
          if (active) {
            setHomeContent(DEFAULT_HOME_CONTENT);
            setHomeContentReady(true);
          }
        });
    }
    return () => { active = false; };
    // Initial SSR props are consumed once; later navigations remount with no initial payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const steps = [
    { icon: BookOpen, label: t("step_choose") },
    { icon: ShieldCheck, label: t("step_paste") },
    { icon: ScanLine, label: t("step_analyze") },
    { icon: ExternalLink, label: t("step_download") },
  ];

  const visibleFaqs = (displayHomeContent?.faqs || []).filter((item) => item.visible !== false && item.question?.trim() && item.answer?.trim());

  const jsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebSite",
        name: settings.site_name || "ARAN TOOLS",
        url: settings.site_origin || (typeof window !== "undefined" ? window.location.origin : ""),
        description,
      },
      ...(visibleFaqs.length ? [{
        "@type": "FAQPage",
          mainEntity: visibleFaqs.map((item) => ({
          "@type": "Question",
            name: item.question,
            acceptedAnswer: { "@type": "Answer", text: item.answer },
        })),
      }] : []),
    ],
  };

  return (
    <div className="relative">
      <Seo
        title={hasAvailableTools
          ? displayHomeContent?.seo_title || settings.seo_title || "أدوات ARAN TOOLS المتاحة — ARAN TOOLS"
          : "المكتبة التعليمية وإرشادات الوسائط — ARAN TOOLS"}
        description={description}
        path="/"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative overflow-hidden pt-14 sm:pt-20 pb-8">
        <div className="accent-glow w-[28rem] h-[28rem] bg-primary/20 -top-32 right-1/4" />
        <div className="accent-glow w-72 h-72 bg-cyan-500/10 top-10 left-1/4" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center relative z-10">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-primary bg-primary/10 border border-primary/20 rounded-full px-4 py-1.5 mb-6 animate-fade-up">
            {t("no_login_required")}
          </span>
          {homeContentReady ? (
            <>
              <h1 className="font-head text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight animate-fade-up" style={{ animationDelay: "60ms" }}>
                {displayHomeContent.hero_title}
              </h1>
              <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl mx-auto animate-fade-up" style={{ animationDelay: "120ms" }}>
                {hasAvailableTools
                  ? homeContent.introduction || settings.site_description || defaultDescription
                  : defaultDescription}
              </p>
              {!hasAvailableTools && (
                <p className="mx-auto mt-4 max-w-xl rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3 text-sm leading-7 text-muted-foreground" role="status" data-testid="download-services-paused">
                  خدمات تحليل الروابط وتنزيل الوسائط متوقفة حالياً. لا ترسل روابط خاصة أو بيانات دخول؛ استخدم خيارات المنصة الرسمية.
                </p>
              )}
            </>
          ) : (
            <div className="mx-auto max-w-2xl space-y-4" role="status" aria-label="جاري تحميل محتوى الصفحة الرئيسية" data-testid="home-content-loading">
              <div className="mx-auto h-12 w-4/5 animate-pulse rounded-lg bg-muted" />
              <div className="mx-auto h-6 w-3/5 animate-pulse rounded-lg bg-muted" />
            </div>
          )}
        </div>
      </section>

      {/* Platform grid */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 relative z-10">
        <PlatformGrid />
      </section>

      {/* The optional ad slot is separate from tool controls and follows the tools section. */}
      <AdsterraNativeBanner />

      {articles.length > 0 && <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16" data-testid="home-learning-section" dir="rtl">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div><h2 className="font-head text-2xl sm:text-3xl font-bold">مقالات وأدلة ARAN TOOLS</h2><p className="mt-2 text-sm leading-7 text-muted-foreground">شروحات عربية تساعدك على فهم روابط الفيديو والخيارات المتاحة وطرق الاستخدام الآمن.</p></div>
          <Link to="/learn" className="text-sm font-semibold text-primary hover:underline">تصفّح المكتبة التعليمية</Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map((article) => <ArticleCard key={article.id || article.slug} article={article} categoryName={article.category_name} />)}
        </div>
      </section>}

      {/* How it works */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16" data-testid="home-how-it-works">
        <h2 className="font-head text-2xl sm:text-3xl font-bold text-center mb-10">
          {t("how_it_works")}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <div key={i} className="aran-card p-6 text-center relative">
              <span className="absolute top-3 right-3 font-mono text-xs text-primary/60">
                0{i + 1}
              </span>
              <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-3">
                <s.icon className="w-6 h-6" />
              </span>
              <div className="font-semibold text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {homeContentReady && <section className="max-w-6xl mx-auto px-4 sm:px-6 mt-16 pb-16" data-testid="home-service-guide" dir="rtl">
        <HomeContentSections content={displayHomeContent} platforms={platforms} />
      </section>}
    </div>
  );
}
